import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Heart, MapPin, ExternalLink } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { orgFeaturePage } from "../utils/orgLinks";
import { VettedLabel } from "./VettedLabel";

// Admin drag order (display_order) rules; everything else is only a tiebreak.
const rank = (o) => (o.featured && o.met_by_thewall ? 0 : o.met_by_thewall ? 1 : o.featured ? 2 : 3);
export const sortOrgs = (a, b) => {
  const da = a.display_order ?? -1, db = b.display_order ?? -1;
  if (da !== db) return da - db;
  if (rank(a) !== rank(b)) return rank(a) - rank(b);
  const pa = a.display_priority ?? 999, pb = b.display_priority ?? 999;
  if (pa !== pb) return pa - pb;
  return (a.name || "").localeCompare(b.name || "");
};

export const OrgCard = ({ org, causes, compact = false, testIdSuffix = "" }) => (
  <Card className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-[#E2E0DC]/60 overflow-hidden group hover:-translate-y-1 flex flex-col" data-testid={`org-card-${testIdSuffix}`}>
    <div className={`${compact ? "aspect-[16/10]" : "aspect-video"} overflow-hidden bg-[#E8E6E1]`}>
      {org.image_url ? (
        <img src={org.image_url} alt={org.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Heart className="h-12 w-12 text-[#2E5C55]/30" /></div>
      )}
    </div>
    <CardContent className={`${compact ? "p-4" : "p-6"} flex-1 flex flex-col`}>
      {org.location && <div className="flex items-center gap-1.5 text-xs text-[#5C5C5C] mb-1"><MapPin className="h-3.5 w-3.5" /> {org.location}</div>}
      <h3 className={`font-['Playfair_Display'] ${compact ? "text-lg" : "text-xl"} font-medium text-[#1A1A1A] mb-2 line-clamp-2`}>{org.name}</h3>
      <VettedLabel vettedBy={org.vetted_by} className="mb-2 self-start" />
      {org.causes?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {org.causes.slice(0, 2).map((causeId) => {
            const cause = causes.find((c) => c.id === causeId);
            return cause ? <span key={causeId} className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: cause.color }}>{cause.name}</span> : null;
          })}
          {org.causes.length > 2 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">+{org.causes.length - 2}</span>}
        </div>
      )}
      <p className={`text-[#5C5C5C] text-sm leading-relaxed mb-3 ${compact ? "line-clamp-2" : "line-clamp-3"}`}>{org.mission_statement}</p>
      <div className="flex gap-2 mt-auto">
        <a href={org.donation_url} target="_blank" rel="noopener noreferrer" className="flex-1">
          <Button className="w-full bg-[#E76F51] hover:bg-[#E76F51]/90 rounded-full text-xs" data-testid={`donate-btn-${testIdSuffix}`}>Donate <ExternalLink className="ml-1.5 h-3 w-3" /></Button>
        </a>
        <Link to={orgFeaturePage(org)?.path || `/organizations/${org.org_id}`}>
          <Button variant="outline" className="rounded-full border-[#E2E0DC] text-xs" data-testid={`details-btn-${testIdSuffix}`}>{orgFeaturePage(org)?.label || "Details"}</Button>
        </Link>
      </div>
    </CardContent>
  </Card>
);

// Cause picker + the full org grid. Pass data in, or let it load itself (guide page).
export const OrgsSection = ({ organizations: orgsProp, causes: causesProp, title = <>Adopt an org &amp; unlock local treasures</>, eyebrow = "Organizations We Support", intro = "Each organization has been vetted and serves communities in Tacoma and the South Sound region.", id = "causes" }) => {
  const [loaded, setLoaded] = useState({ orgs: [], causes: [] });
  const [cause, setCause] = useState("all");
  const selfLoad = !orgsProp;
  useEffect(() => {
    if (!selfLoad) return;
    Promise.all([axios.get(`${API}/organizations`), axios.get(`${API}/causes`)])
      .then(([o, c]) => setLoaded({ orgs: o.data || [], causes: c.data || [] })).catch(() => {});
  }, [selfLoad]);
  const all = orgsProp || loaded.orgs;
  const causes = causesProp || loaded.causes;
  const labeled = (o) => (o.causes || []).some((cid) => causes.some((c) => c.id === cid));
  const counts = Object.fromEntries(causes.map((c) => [c.id, all.filter((o) => (o.causes || []).includes(c.id)).length]));
  const unlabeledCount = all.filter((o) => !labeled(o)).length;
  const list = all.filter((o) => (cause === "all" ? true : cause === "other" ? !labeled(o) : (o.causes || []).includes(cause))).sort(sortOrgs);
  const picked = causes.find((c) => c.id === cause);

  return (
    <section id={id} className="py-24 md:py-32" data-testid="orgs-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="text-sm font-medium tracking-wider uppercase text-[#E76F51] mb-4 block">{eyebrow}</span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-4" data-testid="causes-title">{title}</h2>
          <p className="text-xl leading-relaxed font-light text-[#5C5C5C] max-w-2xl mx-auto">{intro}</p>
        </div>
        {causes.length > 0 && (
          <div className="max-w-2xl mx-auto mb-12 text-center" data-testid="home-cause-picker">
            <p className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A] mb-4">Choose an org that aligns with what you value:</p>
            <Select value={cause} onValueChange={setCause}>
              <SelectTrigger className="h-12 rounded-full border-2 border-[#2E5C55]/30 bg-white text-base px-5" data-testid="home-cause-select"><SelectValue placeholder="All causes" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all" data-testid="home-cause-option-all">All causes ({all.length} organizations)</SelectItem>
                {causes.filter((c) => counts[c.id] > 0).map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`home-cause-option-${c.id}`}>
                    <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: c.color }} />{c.name} ({counts[c.id]})</span>
                  </SelectItem>
                ))}
                {unlabeledCount > 0 && <SelectItem value="other" data-testid="home-cause-option-other">More Community Partners ({unlabeledCount})</SelectItem>}
              </SelectContent>
            </Select>
          </div>
        )}
        {all.length > 0 && (
          <div data-testid="all-orgs-wrap">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#1A1A1A] flex items-center gap-3" data-testid="all-orgs-heading">
                {picked && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: picked.color }} />}
                {cause === "all" ? "All organizations" : cause === "other" ? "More Community Partners" : picked?.name}
              </h3>
              <span className="text-sm text-[#5C5C5C] bg-[#F5F5F5] rounded-full px-3 py-1" data-testid="all-orgs-count">{list.length} {list.length === 1 ? "org" : "orgs"}</span>
            </div>
            {list.length === 0 ? <p className="text-center text-[#5C5C5C] py-10">No organizations in this category yet.</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="all-orgs-grid">
                {list.map((org, i) => <OrgCard key={org.org_id} org={org} causes={causes} compact testIdSuffix={`all-${i}`} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
